<template>
    <div v-if="rulesData" class="mt-4">
        <busy-modal :show="loading" />
        <b-row class="w-100 no-gutters">
            <b-col md="6" class="pr-md-2">
                <form-select
                    v-model="rulesData.arrival_day_rule_enabled"
                    floatingLabel="Arrival Day Rules"
                    @change="setRuleSettings"
                    :leftIcon="['fas', 'arrow-right']"
                    :options="rulesOptions"
                />
            </b-col>
            <b-col md="6">
                <form-select
                    v-model="rulesData.departure_day_rule_enabled"
                    floatingLabel="Deparure Day Rules"
                    @change="setRuleSettings"
                    :leftIcon="['fas', 'arrow-right']"
                    :options="rulesOptions"
                />
            </b-col>
            <b-col>
                <validation-provider
                    name="max_nights"
                    v-slot="{ errors, handleChange }"
                    rules="validPositiveOrZeroInteger|maxValue:1000"
                    
                >
                    <form-input
                        v-model="rulesData.max_nights"
                        @change="setRuleSettingsDebounce"
                        @input="handleChange"
                        floatingLabel="Max Night Stay"
                        :leftIcon="['fas', 'arrow-right']"
                        :empty-value="null"
                        :error="errors[0]"
                    />
                </validation-provider>
            </b-col>
        </b-row>
        <b-row class="w-100 no-gutters">
            <b-col md="12">
                <b-card class="template rate-question-card">
                    <template #header
                        >Please tell the application how you would like to handle each of the following situations.
                    </template>
                    <b-card-body>
                        <b-card-text>
                            An availability gap exists that is &#60; the minimum night rule for the property, how should
                            the application handle this situation?
                        </b-card-text>
                        <b-row class="no-gutters flex-column flex-md-row">
                            <b-col>
                                <form-select
                                    :options="ignoreMinNightsOptions"
                                    v-model="rulesData.ignore_min_nights_rules_if_needed"
                                    @change="setRuleSettings"
                                    :class="{
                                        'mr-md-2': rulesData.ignore_min_nights_rules_if_needed,
                                    }"
                                />
                            </b-col>
                            <b-col v-if="rulesData.ignore_min_nights_rules_if_needed">
                                <form-select
                                    :options="allowBookingOptions"
                                    v-model="rulesData.ignore_min_nights_threshold"
                                    @change="setRuleSettings"
                                />
                            </b-col>
                        </b-row>
                        <b-card-text>
                            An arrival day rule cannot be enforced because that day is already booked. How should the
                            application handle this situation?
                        </b-card-text>
                        <b-row class="no-gutters flex-md-row flex-column">
                            <b-col>
                                <form-select
                                    :options="getIgnoreDayRule(true)"
                                    v-model="rulesData.ignore_arrival_rules_if_needed"
                                    @change="setRuleSettings"
                                    class="mr-md-2"
                                />
                            </b-col>
                            <b-col>
                                <form-select
                                    :options="getIgnoreDayRule(false)"
                                    v-model="rulesData.ignore_departure_rules_if_needed"
                                    @change="setRuleSettings"
                                />
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import api from '../../../../api';
import debounce from 'lodash/debounce';
import { RENTAL_PROPERTY_SETUP_RATES_ENDPOINT } from '../../../../endpoints';
import { formatResponseError } from '../../../../utils/api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import FormSelect from '../../../generic/form-select/form-select.vue';
import FormInput from '../../../generic/form-input/form-input.vue';
export default {
    components: {
        FormSelect,
        FormInput,
        BusyModal,
    },
    inject: ['propertyId'],
    data() {
        return {
            loading: false,
        };
    },
    props: {
        rulesData: {
            type: Object,
            default: null,
        },
    },
    computed: {
        rulesOptions() {
            return [
                { text: 'Disabled', value: false },
                { text: 'Enabled', value: true },
            ];
        },
        ignoreMinNightsOptions() {
            return [
                {
                    text: 'Do not ignore Minimum Night rule. Guest will not be able to book this property online.',
                    value: false,
                },
                {
                    text: 'Ignore Minimum Night rule and allow guest to book online.',
                    value: true,
                },
            ];
        },
        allowBookingOptions() {
            return Array.from({ length: 5 }, (v, k) => k + 1).map((item) => ({
                value: item,
                text: `Allow to book if reservation is at least ${item}night${item > 1 ? 's' : ''}`,
            }));
        },
    },
    methods: {
        getIgnoreDayRule(isArrival) {
            return [
                {
                    text: `Do not ignore ${
                        isArrival ? 'Arrival' : 'Departure'
                    } Day rule. Guest will not be able to book this property online.`,
                    value: false,
                },
                {
                    text: `Ignore ${isArrival ? 'Arrival' : 'Departure'} Day rule and allow guest to book online.`,
                    value: true,
                },
            ];
        },
        setRuleSettingsDebounce: debounce(function () {
            this.setRuleSettings();
        }, 1000),
        async setRuleSettings() {
            if (!this.loading) {
                try {
                    this.loading = true;
                    await api.post(RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId).setRulesSettings, {
                        ...this.rulesData,
                    });
                    this.$emit('reload-all-data');
                    this.successToast({
                        text: 'Rules settings successfuly saved',
                    });                    
                } catch (e) {
                    this.errorToast({
                        text: 'Could not set rules settings',
                    });
                } finally {
                    this.loading = false;
                }
            }
        },
    },
};
</script>
